/**
 * posts.ts
 * Unified API utility for creating and managing post-backed entities.
 * All entity kinds (impact, teabate, solution, action, feedback, collection) route through
 * the single apiRoutes.posts.root endpoint for CRUD.
 */
import api from "config/axiosConfig";
import { saveAnonToken, AnonEntity } from "util/anonTokenVault";
import { addAnonRef } from "util/anonIndex";
import { uploadEntityMediaDirect } from "util/entityMediaUpload";
import { apiRoutes } from "util/apiRoutes";
import type { Action, Impact, Solution } from "util/constants";

export type EntityKind = "impact" | "teabate" | "solution" | "action" | "feedback" | "collection";

export interface CollectionPostResponse extends Record<string, unknown> {
  id: number;
  kind: "collection";
  title: string | null;
  body?: string | null;
  url?: string | null;
  issue_id?: number | null;
  properties?: Record<string, unknown>;
}

/** Canonical placeholder when title is disabled or empty (server + client). */
export const NO_TITLE_PLACEHOLDER = "[no_title]";

/**
 * Normalizes title for post create: when `showTitle` is false, always `[no_title]`;
 * when true, uses trimmed input or `[no_title]` if blank.
 */
export function normalizePostTitle(showTitle: boolean, raw: string): string {
  return showTitle ? (raw.trim() || NO_TITLE_PLACEHOLDER) : NO_TITLE_PLACEHOLDER;
}

/**
 * Discriminated union for unified create — all kinds post to `apiRoutes.posts.root`.
 */
export type CreatePostRequest =
  | {
      kind: "impact";
      issueId: number;
      title: string | null;
      body: string | null;
      url: string | null;
      polarity: number | null;
      audience: string;
      properties: Record<string, unknown>;
      scope?: string | null;
    }
  | {
      kind: "teabate";
      issueId: number;
      title: string | null;
      body: string | null;
      audience: string;
      properties: Record<string, unknown>;
    }
  | {
      kind: "solution";
      issueId: number;
      title: string | null;
      body: string | null;
      url: string | null;
      audience: string;
      properties: Record<string, unknown>;
    }
  | {
      kind: "action";
      issueId: number | null;
      title: string | null;
      body: string | null;
      audience: string;
      properties: Record<string, unknown>;
    }
  | {
      kind: "collection";
      issueId: number | null;
      title: string;
      body: string | null;
      url: string | null;
      audience: string;
      properties: Record<string, unknown>;
      status?: "draft" | "published" | "scheduled" | "archived";
    };

/**
 * Single entry point for creating post-backed entities (impact, teabate, solution, action, collection).
 * Feedback continues to use `createFeedback` directly from feedback UI.
 */
export async function createPost(req: CreatePostRequest): Promise<Record<string, unknown>> {
  switch (req.kind) {
    case "impact": {
      const impact = await createImpactForIssue(req.issueId, {
        title: req.title,
        body: req.body,
        url: req.url,
        polarity: req.polarity,
        audience: req.audience,
        properties: req.properties,
        scope: req.scope ?? null,
      });
      return { ...impact } as Record<string, unknown>;
    }
    case "teabate": {
      const teabate = await createTeabateForIssue(req.issueId, {
        title: req.title,
        body: req.body,
        audience: req.audience,
        properties: req.properties,
      });
      return { ...teabate } as Record<string, unknown>;
    }
    case "solution": {
      const solution = await createSolutionForIssue(req.issueId, {
        title: req.title,
        body: req.body,
        url: req.url,
        audience: req.audience,
        properties: req.properties,
      });
      return { ...solution } as Record<string, unknown>;
    }
    case "action": {
      const action = await createAction({
        issueId: req.issueId,
        title: req.title,
        body: req.body,
        audience: req.audience,
        properties: req.properties,
      });
      return { ...action } as Record<string, unknown>;
    }
    case "collection": {
      const collection = await createCollection({
        issueId: req.issueId,
        title: req.title,
        body: req.body,
        url: req.url,
        audience: req.audience,
        properties: req.properties,
      });
      return { ...collection } as Record<string, unknown>;
    }
  }
}



/* ------------------------------------------------------------------ */
/*  Media upload (uses concrete kind directly – no teabate→impact)    */
/* ------------------------------------------------------------------ */

export async function uploadEntityMedia(
  kind: EntityKind,
  id: number,
  files: File[],
  orders: number[]
) {
  if (files.length === 0) return;
  await uploadEntityMediaDirect(
    kind,
    id,
    files.map((file, index) => ({
      file,
      order: Number.isFinite(orders[index]) ? orders[index] : index + 1,
    }))
  );
}

/* ------------------------------------------------------------------ */
/*  Helpers: save anon token from create response                     */
/* ------------------------------------------------------------------ */

function handleAnonToken(kind: AnonEntity, data: Record<string, unknown>) {
  if (
    data?.properties &&
    (data.properties as Record<string, unknown>)?.privacy === "anonymous" &&
    typeof data.anon_edit_token === "string"
  ) {
    const id = (data.id as number) ?? 0;
    void saveAnonToken(kind, id, data.anon_edit_token as string);
    addAnonRef({ type: kind, id });
  }
}

/* ------------------------------------------------------------------ */
/*  Create functions — all route through POST apiRoutes.posts.root              */
/* ------------------------------------------------------------------ */

export async function createImpactForIssue(
  issueId: number,
  payload: Record<string, unknown>
): Promise<Impact> {
  const resp = await api.post<Impact>(apiRoutes.posts.root, {
    kind: "impact",
    status: payload.status ?? undefined,
    issueId,
    title: payload.title ?? null,
    body: payload.body ?? null,
    url: payload.url ?? null,
    polarity: payload.polarity ?? null,
    audience: payload.audience ?? payload.privacy ?? "public",
    properties: {
      ...(payload.properties ?? payload),
      scope: payload.scope ?? null,
    },
  });
  const d = resp.data;
  handleAnonToken("impact", { ...d } as Record<string, unknown>);
  return d;
}

export async function createTeabateForIssue(
  issueId: number,
  payload: Record<string, unknown>
): Promise<Impact> {
  const resp = await api.post<Impact>(apiRoutes.posts.root, {
    kind: "teabate",
    status: payload.status ?? undefined,
    issueId,
    title: payload.title ?? null,
    body: payload.body ?? null,
    audience: payload.audience ?? payload.privacy ?? "public",
    // `kind: "teabate"` above is the canonical post-unification discriminator.
    // The legacy `properties.is_teabate` boolean is no longer written.
    properties: payload.properties ?? payload,
  });
  const d = resp.data;
  handleAnonToken("teabate", { ...d } as Record<string, unknown>);
  return d;
}

export async function createSolutionForIssue(
  issueId: number,
  payload: Record<string, unknown>
): Promise<Solution> {
  const resp = await api.post<Solution>(apiRoutes.posts.root, {
    kind: "solution",
    status: payload.status ?? undefined,
    issueId,
    title: payload.title ?? null,
    body: payload.body ?? null,
    url: payload.url ?? null,
    audience: payload.audience ?? payload.privacy ?? "public",
    properties: payload.properties ?? payload,
    // Inline principle drafts are attached atomically by the backend
    // using the just-created post as the trusted host. This avoids a
    // separate `/api/posts/:id/principles` round-trip that would fail
    // for anonymous solutions (no anon-edit-token at attach time).
    principles: payload.principles ?? undefined,
  });
  const d = resp.data;
  handleAnonToken("solution", { ...d } as Record<string, unknown>);
  return d;
}

export async function createAction(payload: Record<string, unknown>): Promise<Action> {
  const resp = await api.post<Action>(apiRoutes.posts.root, {
    kind: "action",
    status: payload.status ?? undefined,
    issueId: payload.issueId ?? null,
    title: payload.title ?? null,
    body: payload.body ?? null,
    audience: payload.audience ?? payload.privacy ?? "public",
    properties: payload.properties ?? payload,
    // See `createSolutionForIssue`: tasks are attached inline so
    // anonymous actions don't need an anon-token round-trip.
    tasks: payload.tasks ?? undefined,
  });
  const d = resp.data;
  handleAnonToken("action", { ...d } as Record<string, unknown>);
  return d;
}

export async function createCollection(
  payload: Record<string, unknown>
): Promise<CollectionPostResponse> {
  const resp = await api.post<CollectionPostResponse>(apiRoutes.posts.root, {
    kind: "collection",
    status: payload.status ?? undefined,
    issueId: payload.issueId ?? null,
    title: payload.title ?? null,
    body: payload.body ?? null,
    url: payload.url ?? null,
    audience: payload.audience ?? payload.privacy ?? "me",
    properties: payload.properties ?? payload,
  });
  const d = resp.data;
  handleAnonToken("collection", { ...d } as Record<string, unknown>);
  return d;
}

export async function createFeedback(payload: Record<string, unknown>) {
  const resp = await api.post(apiRoutes.posts.root, {
    kind: "feedback",
    title: payload.title ?? null,
    body: payload.body ?? null,
    audience: payload.audience ?? payload.privacy ?? "public",
    properties: payload.properties ?? payload,
  });
  const d = resp.data as Record<string, unknown>;
  handleAnonToken("feedback", d);
  return d;
}

/* ------------------------------------------------------------------ */
/*  Coauthors — uses concrete kind directly                           */
/* ------------------------------------------------------------------ */

export async function addCoauthors(
  kind: EntityKind,
  id: number,
  coauthors: Array<{ id: number; type: string }>
) {
  if (coauthors.length === 0) return;
  await api.post(apiRoutes.coauthors.request(kind, id), {
    addedCoauthors: coauthors,
  });
}

/* ------------------------------------------------------------------ */
/*  Wikipedia URL helper                                              */
/* ------------------------------------------------------------------ */

export const standardizeWikipediaUrl = (raw?: string | null) => {
  if (!raw) return null;
  try {
    const url = new URL(raw.startsWith("http") ? raw : `https://${raw}`);
    const path = url.pathname.startsWith("/wiki/")
      ? url.pathname
      : `/wiki/${url.pathname.replace(/^\//, "")}`;
    return `https://en.wikipedia.org${path}`;
  } catch {
    return raw.replace(
      /^https?:\/\/(www\.)?wikipedia\.org\/wiki\//,
      "https://en.wikipedia.org/wiki/"
    );
  }
};
